import { useForm } from "react-hook-form";
import type { MutateOptions } from "react-query";
import type { OpenCall, OpenCallFormFields } from "../types";
import { useCreateOpenCall } from "./useCreateOpenCall";
import { useUpdateOpenCall } from "./useUpdateOpenCall";

type UseOpenCallFormParams = {
  openCall?: OpenCall;
} & MutateOptions<OpenCall, unknown, OpenCallFormFields>;

const truncateDateTime = (dateTime?: string) =>
  dateTime ? dateTime.slice(0, 10) : undefined;

/**
 * Custom hook to handle form validation and submission for creating/updating an OpenCall
 */
const useOpenCallForm = ({ openCall, ...options }: UseOpenCallFormParams) => {
  const { handleSubmit, ...form } = useForm<OpenCallFormFields>({
    // Fill fields with original data if we're editing, otherwise set defaults for new OpenCall
    defaultValues: openCall
      ? {
          ...openCall,
          submissionStartDate: truncateDateTime(openCall.submissionStartDate),
          submissionEndDate: truncateDateTime(openCall.submissionEndDate),
          communityVotingStartDate: truncateDateTime(
            openCall.communityVotingStartDate,
          ),
          communityVotingEndDate: truncateDateTime(
            openCall.communityVotingEndDate,
          ),
          exhibitionStartDate: truncateDateTime(openCall.exhibitionStartDate),
          exhibitionEndDate: truncateDateTime(openCall.exhibitionEndDate),
        }
      : { status: "DRAFT" },
  });

  const createMutation = useCreateOpenCall();
  const updateMutation = useUpdateOpenCall(openCall?.id ?? "undefined");

  const isEdit = !!openCall;
  const mutation = isEdit ? updateMutation : createMutation;

  const onSubmit = handleSubmit(
    ({ partnerLinks, partnerLogo, requirements, ...formData }) => {
      // Transform required field values to request data if necessary
      const transformed: OpenCallFormFields = {
        ...formData,
        partnerLinks: partnerLinks?.filter(({ url }) => url !== "") ?? [],
        // Partner logos are optional, so set undefined if URL is an empty string
        partnerLogo: partnerLogo?.url === "" ? undefined : partnerLogo,
      };

      // Add optional fields if they exist, otherwise omit from request body
      if (requirements) {
        transformed.requirements = requirements;
      }

      transformed.submissionEndDate = transformed.submissionEndDate?.length
        ? transformed.submissionEndDate
        : undefined;
      transformed.communityVotingStartDate = transformed
        .communityVotingStartDate?.length
        ? transformed.communityVotingStartDate
        : undefined;
      transformed.communityVotingEndDate = transformed.communityVotingEndDate
        ?.length
        ? transformed.communityVotingEndDate
        : undefined;
      transformed.exhibitionStartDate = transformed.exhibitionStartDate?.length
        ? transformed.exhibitionStartDate
        : undefined;
      transformed.exhibitionEndDate = transformed.exhibitionEndDate?.length
        ? transformed.exhibitionEndDate
        : undefined;

      mutation.mutate(
        isEdit ? { ...openCall, ...transformed } : transformed,
        options,
      );
    },
  );

  return {
    ...form,
    isEdit,
    mutation,
    onSubmit,
  };
};

export { useOpenCallForm };
